import { createIcon } from './icon.js';

export interface ContactData {
  id: number;
  name: string;
  role: string | null;
  phone: string | null;
  maxUrl: string | null;
  description: string | null;
  groupId: number | null;
  tags: string[];
  sortOrder: number;
}

export interface ContactGroupData {
  id: number;
  name: string;
  slug: string;
  icon: string | null;
  color: string;
  sortOrder: number;
}

export function createContactCard(contact: ContactData): HTMLElement {
  const card = document.createElement('div');
  card.className = 'contact-card';

  const left = document.createElement('div');
  left.className = 'contact-card__info';

  const name = document.createElement('div');
  name.className = 'contact-card__name';
  name.textContent = contact.name;
  left.appendChild(name);

  if (contact.role) {
    const role = document.createElement('div');
    role.className = 'contact-card__role';
    role.textContent = contact.role;
    left.appendChild(role);
  }

  if (contact.phone) {
    const phone = document.createElement('div');
    phone.className = 'contact-card__phone';
    phone.textContent = contact.phone;
    left.appendChild(phone);
  }

  card.appendChild(left);

  // Action buttons
  const actions = document.createElement('div');
  actions.className = 'contact-card__actions';

  if (contact.phone) {
    const phoneBtn = document.createElement('a');
    phoneBtn.className = 'contact-card__btn';
    phoneBtn.href = `tel:${contact.phone}`;
    phoneBtn.setAttribute('aria-label', `Позвонить ${contact.name}`);
    phoneBtn.appendChild(createIcon('phone', 20));
    actions.appendChild(phoneBtn);
  }

  if (contact.maxUrl) {
    const maxBtn = document.createElement('a');
    maxBtn.className = 'contact-card__btn contact-card__btn--max';
    maxBtn.href = contact.maxUrl;
    maxBtn.target = '_blank';
    maxBtn.rel = 'noopener';
    maxBtn.setAttribute('aria-label', `Написать в МАХ ${contact.name}`);
    maxBtn.textContent = 'М';
    actions.appendChild(maxBtn);
  }

  card.appendChild(actions);

  // Expandable description
  if (contact.description) {
    card.style.cursor = 'pointer';
    const desc = document.createElement('div');
    desc.className = 'contact-card__desc';
    desc.textContent = contact.description;
    desc.style.display = 'none';
    card.appendChild(desc);

    card.addEventListener('click', () => {
      const isHidden = desc.style.display === 'none';
      desc.style.display = isHidden ? 'block' : 'none';
    });
  }

  return card;
}

export function createContactGroupSection(
  group: ContactGroupData,
  contacts: ContactData[],
  filterTag?: string | null,
): HTMLElement | null {
  const filtered = filterTag
    ? contacts.filter(c => c.tags.includes(filterTag))
    : contacts;

  if (filtered.length === 0) return null;

  const section = document.createElement('div');
  section.className = `contact-section contact-section--${group.color}`;

  const header = document.createElement('h2');
  header.className = 'contact-section__title';
  header.textContent = group.name;
  section.appendChild(header);

  for (const contact of filtered) {
    section.appendChild(createContactCard(contact));
  }

  return section;
}

export function renderContactTags(contacts: ContactData[]): string[] {
  const tags = new Set<string>();
  for (const c of contacts) {
    for (const tag of c.tags) {
      tags.add(tag);
    }
  }
  return Array.from(tags).sort();
}

export function createTagChips(
  tags: string[],
  activeTag: string | null,
  onSelect: (tag: string | null) => void,
): HTMLElement {
  const container = document.createElement('div');
  container.className = 'tag-chips';

  // "All" chip
  const allChip = document.createElement('button');
  allChip.className = `tag-chip${activeTag === null ? ' tag-chip--active' : ''}`;
  allChip.textContent = 'Все';
  allChip.addEventListener('click', () => onSelect(null));
  container.appendChild(allChip);

  for (const tag of tags) {
    const chip = document.createElement('button');
    chip.className = `tag-chip${activeTag === tag ? ' tag-chip--active' : ''}`;
    chip.textContent = tag;
    chip.addEventListener('click', () => onSelect(tag));
    container.appendChild(chip);
  }

  return container;
}
